import { KeyboardEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    AskQuestionLabel,
    Button, equalInLC,
    FCX, getRem,
    Popup,
    ToolSearch as PlatformToolSearch,
    ToolSearchContent,
    ToolSearchHeader,
    ToolSearchInput,
    usePlatformFeatureFlag,
} from "@datassential/platform-ui-lib";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import cn from "classnames";
import { IoClose } from "react-icons/io5";
import { BsStars } from "react-icons/bs";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useSearchResults } from "@apiHooks/useSearchResults";
import { createRecentSearches } from "@apiHooks/useRecentSearches";
import { useAiSearchPage } from "@apiHooks/useAiSearchPage";
import { AI_SEARCH_PAGE_ROUTES, getSearchUrl, Routes } from "@core/routes";
import { SearchResults } from "@models/SearchResults";
import { currentCountrySelector } from "@selectors/countries.selector";
import SearchResultsSections from "./SearchResults/SearchResultsSections";
import SuggestedSearches from "./SuggestedSearches";
import RecentSearches from "./RecentSearches";
import { GlobalContext } from "@components/App/App.context";
import { useRestaurantMenu } from "@vms/RestaurantMenu";
import { useFilters } from "@vms/SelectFilters/SelectFiltersContext";
import { FaArrowRight } from "react-icons/fa";
import { AI_SEARCH_ROUTE_PARAM, BYO_WORDS_QUERY_PARAM_NAME, FEATURE_AI_SEARCH_DS_AI } from "@constants/general";

const EMPTY_SEARCH_RESULTS = new SearchResults();

interface ToolSearchProps {
    isOpen: boolean;
    close: () => void;
}

const ToolSearch: FCX<ToolSearchProps> = ({ isOpen, close, className, testId }) => {
    const navigate = useNavigate();
    const { commonData: { data: suggestedSearches } } = useContext(GlobalContext);
    const { rawFilterData } = useRestaurantMenu();
    const { activeRestaurantListId } = useFilters();

    const [search, setSearch] = useState<string>('');
    const [isApplyingSearch, setIsApplyingSearch] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const currentCountry = useSelector(currentCountrySelector);
    const pageRequestIsActualRef = useRef<boolean>(true);
    const { getPage } = useAiSearchPage();
    const isDsAiSearchEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH_DS_AI);
    const trimmedInput = search.trim();

    const isAiSearchEnabled = equalInLC(currentCountry, "usa") && activeRestaurantListId === rawFilterData.defaultRestaurantListId;

    useEffect(() => {
        if (!isApplyingSearch) setSearchQuery(search);
    }, [search, isApplyingSearch]);

    const searchResults = useSearchResults(currentCountry, searchQuery);
    const { data: { data } } = trimmedInput ? searchResults : { data: EMPTY_SEARCH_RESULTS };

    const applyAiSearch = useCallback(async (text: string) => {
        if (isDsAiSearchEnabled) {
            // Hand off to the AI-search route, which holds a loader until the stream
            // says which page the answer belongs on. Navigating immediately means the
            // header doesn't have to block on a round trip of its own.
            navigate(`${Routes.aiSearch}?${AI_SEARCH_ROUTE_PARAM}=${encodeURIComponent(text)}`);
            setSearchQuery("");
            close();
            return;
        }

        setIsApplyingSearch(true);

        try {
            const page = await getPage(text);
            const route = AI_SEARCH_PAGE_ROUTES[page];

            if (pageRequestIsActualRef.current) {
                if (route) {
                    navigate(getSearchUrl(page, route, text));
                }

                createRecentSearches(text);
            }
        } finally {
            setIsApplyingSearch(false);
            setSearchQuery("");
            close();
        }
    }, [isDsAiSearchEnabled, getPage, navigate, close]);

    const applySearch = useCallback(
        (text: string) => {
            if (isAiSearchEnabled) {
                applyAiSearch(text);
            } else {
                navigate(`${Routes.byo}?${BYO_WORDS_QUERY_PARAM_NAME}=${encodeURIComponent(text)}`);
                close();
            }
        },
        [isAiSearchEnabled, applyAiSearch, navigate, close]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter' && search.trim()) applySearch(search);
        },
        [search, applySearch]
    );

    const handleSearchClick = useCallback((text: string) => {
        setSearch(text);
        applySearch(text);
    }, [applySearch]);

    useEffect(() => {
        return () => {
            pageRequestIsActualRef.current = !isOpen;
        }
    }, [isOpen]);

    const submitEl = useMemo(
        () => {
            if (!search.trim().length) return null;

            if (isAiSearchEnabled) {
                return (
                    <Button
                        className="ToolSearch__aiSearchButton appears"
                        onClick={() => {
                            applySearch(search);
                        }}
                        isDisabled={isApplyingSearch}
                    >
                        {isApplyingSearch ? (
                            <AiOutlineLoading3Quarters className="ToolSearch__spinner"/>
                        ) : (
                            <BsStars/>
                        )}
                        &nbsp;AI Search
                    </Button>
                );
            }

            return (
                <Button
                    className="ToolSearch__defaultSearchButton appears"
                    modifiers={["naked"]}
                    onClick={() => {
                        applySearch(search);
                    }}
                >
                    <FaArrowRight className="ToolSearch__defaultSearchButtonIcon"/>
                </Button>
            );
        },
        [search, isAiSearchEnabled, isApplyingSearch, applySearch]
    );

    const handleClose = useCallback(() => {
        setSearch("");
        close();
    }, [close]);

    const filteredSections = useMemo(
        () => data.filter(section => section.items.length > 0),
        [data]
    );

    console.log("searchResults loading:", searchResults.isLoading);

    return (
        <Popup
            isOpened={isOpen}
            close={handleClose}
            closeOnEsc
            noWrapper
            testId={testId}
        >
            <PlatformToolSearch
                className={cn("ToolSearch", className)}
                testId="blockToolSearch"
            >
                <ToolSearchHeader
                    closeEl={
                        <div
                            className="ToolSearch__close"
                            onClick={handleClose}
                        >
                            <IoClose/>
                        </div>
                    }
                    className="appears-bottom"
                >
                    <AskQuestionLabel
                        className="ToolSearch__question-label ToolSearchQuestionLabel"
                        hideLabel={!isAiSearchEnabled}
                        style={{
                            paddingBottom: isAiSearchEnabled ? undefined : getRem(15),
                        }}
                    >
                        {isAiSearchEnabled && 'or type in any food, flavor, or beverage to get started.'}
                        {!isAiSearchEnabled && 'Type in any food, flavor, or beverage to get started.'}
                    </AskQuestionLabel>
                    {isAiSearchEnabled && (
                        <SuggestedSearches
                            data={suggestedSearches}
                            onSearchClick={handleSearchClick}
                        />
                    )}
                    <ToolSearchInput
                        className="ToolSearch__input ToolSearchInput"
                        value={search}
                        onChange={(e) => setSearch(e.currentTarget.value)}
                        onKeyDown={handleKeyDown}
                        submitEl={submitEl}
                    />
                </ToolSearchHeader>
                <ToolSearchContent className="ToolSearch__content appears">
                    <SearchResultsSections sections={filteredSections} search={trimmedInput}/>
                    {!isAiSearchEnabled && !searchResults.isLoading && filteredSections.length === 0 && trimmedInput !== '' && (
                        <div className="ToolSearch__noResultsMessage appears-top">
                            Nothing found. Try searching something else
                        </div>
                    )}
                    {isAiSearchEnabled && (
                        <RecentSearches
                            className={cn(filteredSections.length > 0 && "is-hidden")}
                            onSearchClick={handleSearchClick}
                        />
                    )}
                </ToolSearchContent>
            </PlatformToolSearch>
        </Popup>
    );
};

export default ToolSearch;